import { memo, useMemo } from 'react';

import { faArrowsRotate, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import styled, { keyframes } from 'styled-components';

import { useBoolean, useEvent } from 'core/hooks';
import { Accordion, Icon } from 'core/ui';

import { WorklistSidebarProps } from '../types';
import { WorklistAddEditModal } from './WorklistAddEditModal';
import { WorklistSidebarAccordionItem } from './WorklistSidebarAccordionItem';

export const WorklistSidebar = memo<WorklistSidebarProps>(
  ({
    allWorklists,
    allCounts,
    selectedWorklistId,
    showSidebar,
    currentDataState,
    currentColumnsState,
    onShowSidebarChange,
    onSelectedWorklistIdChange,
    onRefreshClick,
    onCreateWorklist,
    onUpdateWorklist,
    onDeleteWorklist,
    filterType,
    patientId,
    isReadOnly = false,
  }) => {
    const finalFilterType = filterType ?? 'field';
    const [showEditModal, { setTrue: openEditModal, setFalse: closeEditModal }] = useBoolean(false);

    const handleCollapseClick = useEvent(() => {
      if (showSidebar) {
        onShowSidebarChange(false);
      }
    });

    // Determine default active keys based on expanded state
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const defaultActiveKeys = useMemo(() => {
      if (allWorklists == null) return [];

      return [];
    }, [allWorklists]);

    const sortedTopLevelWorklists = useMemo(
      () => allWorklists?.filter((w) => w.parentViewId == null).sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)) ?? [],
      [allWorklists],
    );

    if (!showSidebar || allWorklists == null) {
      return null;
    }

    return (
      <>
        <StyledWrapperDiv>
          <StyledHeaderDiv>
            <StyledTitleDiv onClick={handleCollapseClick}>{filterType == 'field' ? 'WORKLIST' : filterType == 'tag' ? 'CATEGORIES' : 'FILTER'}</StyledTitleDiv>
            <StyledHeaderButtonsDiv>
              <StyledRefreshIcon icon={faArrowsRotate} onClick={onRefreshClick} />
              {!isReadOnly && <StyledPlusIcon icon={faPlus} onClick={openEditModal} />}
              <StyledCollapseSidebarIcon icon={faXmark} onClick={handleCollapseClick} />
            </StyledHeaderButtonsDiv>
          </StyledHeaderDiv>

          <StyledAccordion alwaysOpen>
            {sortedTopLevelWorklists.map((worklist) => {
              return (
                <WorklistSidebarAccordionItem
                  key={worklist.id}
                  selectedWorklistId={selectedWorklistId}
                  allWorklists={allWorklists}
                  allCounts={allCounts}
                  worklistId={worklist.id}
                  onWorklistClick={onSelectedWorklistIdChange}
                  filterType={finalFilterType}
                  patientId={patientId}
                />
              );
            })}
          </StyledAccordion>
        </StyledWrapperDiv>
        {showEditModal && allWorklists && !isReadOnly && (
          <WorklistAddEditModal
            allWorklists={allWorklists}
            currentDataState={currentDataState}
            currentColumnsState={currentColumnsState}
            onClose={closeEditModal}
            onCreateWorklist={onCreateWorklist}
            onUpdateWorklist={onUpdateWorklist}
            onDeleteWorklist={onDeleteWorklist}
            filterType={finalFilterType}
            patientId={patientId}
          />
        )}
      </>
    );
  },
);

WorklistSidebar.displayName = 'WorklistSidebar';

// Enhanced animations for smoother sidebar experience
const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const StyledWrapperDiv = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  animation: ${slideInFromLeft} 0.3s ease-out;
  background: ${({ theme }) => theme.colors.palette.white};
  border-radius: ${({ theme }) => theme.radii.base};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const StyledTitleDiv = styled.div`
  color: ${({ theme }) => theme.colors.palette.grayscale[8]};
  font-weight: ${({ theme }) => theme.fontWeights.bold}; /* Increased from semiBold */
  font-size: ${({ theme }) => theme.fontSizes.subheading}; /* Increased font size */
  cursor: pointer;
  user-select: none;
  padding-left: 1em;
  align-content: center;

  &:hover {
    opacity: 0.8;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const StyledHeaderDiv = styled.div`
  color: ${({ theme }) => theme.colors.palette.grayscale[8]};
  display: flex;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing10};
  user-select: none;
  background: ${({ theme }) => theme.colors.palette.grayscale[1]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
`;

const StyledHeaderButtonsDiv = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10}; /* Consistent spacing for all buttons */
`;

const StyledPlusIcon = styled(Icon)`
  cursor: pointer;
  transition: all 0.2s ease;
  padding: ${({ theme }) => theme.space.spacing10};
  border-radius: ${({ theme }) => theme.radii.base};

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
    opacity: 0.8;
    background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  }

  /* Make it larger on mobile for better touch interaction */
  @media (max-width: 768px) {
    min-width: 48px;
    min-height: 48px;
    padding: ${({ theme }) => theme.space.spacing20};
    font-size: 18px; /* Larger icon size */
  }
`;

const StyledRefreshIcon = styled(Icon)`
  cursor: pointer;
  transition: all 0.2s ease;
  padding: ${({ theme }) => theme.space.spacing10};
  border-radius: ${({ theme }) => theme.radii.base};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: rotate(90deg);
    opacity: 0.8;
    background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  }

  /* Make it larger on mobile for better touch interaction */
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing20};
    font-size: 18px; /* Larger icon size */
  }
`;

const StyledCollapseSidebarIcon = styled(Icon)`
  cursor: pointer;
  transition: all 0.2s ease;
  padding: ${({ theme }) => theme.space.spacing10};
  border-radius: ${({ theme }) => theme.radii.base};
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateX(-2px);
    opacity: 0.8;
    background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  }

  /* Make it larger on mobile for better touch interaction */
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing20};
    font-size: 18px; /* Larger icon size */
  }
`;

const StyledAccordion = styled(Accordion)`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  padding-bottom: 20px;

  &&,
  && .accordion-title {
    font-size: ${({ theme }) => theme.fontSizes.body}; /* Increased from subheading */
  }

  && .accordion-button,
  .accordion-body {
    padding: 0;
  }

  /* Improve scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.palette.grayscale[4]};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.colors.palette.grayscale[5]};
    }
  }
`;
